import {
  Box,
  Button,
  Card,
  Container,
  LayoutVertical,
  Loader,
  ModalBasic,
  SnackbarAlert,
  Table,
  TableBody,
  TableCell,
  TableCellHead,
  TableCols,
  TableExpandContent,
  TableExpandTrigger,
  TableRow,
  Typography,
} from "@/components";
import { FetchContext as context } from "@/contexts/FetchContext";
import { useNavigationError } from "@/hooks/useNavigationError";
import { useTableExpand } from "@/hooks/useTableExpand";
import { PointTransferSummary, pointTransferSummaryDefaultValues } from "@/types/pointTransferSummary";
import { axiosAccountInstance } from "@/utils/axios";
import { COLORS } from "@/utils/colors";
import { formatRequestDateTime, toCommaString } from "@/utils/formatter";
import { NavigateBefore, NavigateNext } from "@mui/icons-material";
import axios from "axios";
import { format } from "date-fns";
import { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { DownloadSummaryPDFButton } from "./components";
import { SendSummaryPDFMailButton } from "./components/SendSummaryPDFMailButton";
import getPortalType from "@/utils/getPortalType";

export const TransferSummary = () => {
  const { state } = useContext(context);
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [pointTransferSummary, setPointTransferSummary] = useState<PointTransferSummary>(
    pointTransferSummaryDefaultValues
  );
  const [feeAmount, setFeeAmount] = useState(0);
  const { navigateError } = useNavigationError();
  const { triggerProps, contentProps } = useTableExpand();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const now = new Date();

  // チップ付与開始日(2023/10/12)
  const tipGrantStartDate = new Date(2023, 9, 12);
  const { colSpan } = triggerProps("breakdown");

  const handleErrors = (error: any) => {
    if (axios.isAxiosError(error)) {
      navigateError({
        error: error,
        endpoint: "/api/point/transfer/summary",
        appName: "POINT_TRANSFER_SUMMARY",
        path: "points",
      });
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: need not to watch
  useEffect(() => {
    const fetch = async () => {
      setIsLoading(true);
      try {
        const startAt = formatRequestDateTime(getStartOfYear(selectedYear));
        const endAt = getEndOfYear(selectedYear);
        const response = await axiosAccountInstance.get(`/api/point/transfer/summary`, {
          params: {
            start_at: startAt,
            ...(endAt && { end_at: formatRequestDateTime(endAt) }),
          },
        });
        setPointTransferSummary(response.data);
        setFeeAmount(response.data.transfer_fee + response.data.error_fee);
      } catch (error) {
        handleErrors(error);
      } finally {
        setIsLoading(false);
      }
    };
    fetch();
  }, [selectedYear]);

  const getEndOfYear = (year: number) => {
    if (year === now.getFullYear()) {
      return null;
    } else {
      return new Date(year, 11, 31, 23, 59, 59, 999);
    }
  };

  const getStartOfYear = (year: number) => {
    if (year === tipGrantStartDate.getFullYear()) {
      return tipGrantStartDate;
    }
    return new Date(year, 0, 1);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };
  return (
    <Container>
      {state.loading || isLoading ? (
        <Loader />
      ) : (
        <>
          <Helmet title={t("POINT_TRANSFER_SUMMARY")} />

          <ModalBasic
            open={isModalOpen}
            onClose={closeModal}
            title={t("EMAIL_SENT_SUCCESS")}
            weakActionButton={{
              label: t("CLOSE"),
              onClick: closeModal,
            }}
          >
            <p>
              {getPortalType() === "crew"
                ? t("EMAIL_SENT_TO_CREW_REGISTERED_ADDRESS")
                : t("EMAIL_SENT_TO_DRIVER_REGISTERED_ADDRESS")}
              <br />
              <br />
              {t("EMAIL_ADDRESS_CONFIRMABLE_ON_MY_PAGE")}
            </p>
          </ModalBasic>
          <SnackbarAlert
            type="danger"
            message={t("EMAIL_SEND_DAILY_LIMIT_EXCEEDED")}
            onClose={() => {
              setOpenSnackbar(false);
            }}
            open={openSnackbar}
          />

          <LayoutVertical>
            <Typography variant="h1" align="left">
              {t("POINT_TRANSFER_SUMMARY")}
            </Typography>
          </LayoutVertical>

          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "space-between", sm: "center" },
              alignItems: "center",
              alignSelf: "stretch",
              padding: "24px 0px",
            }}
          >
            {selectedYear === tipGrantStartDate.getFullYear() ? (
              <Box sx={{ width: "48px", height: "48px" }} />
            ) : (
              <Button
                onClick={() => setSelectedYear(selectedYear - 1)}
                sx={{ display: "flex", minWidth: "48px", height: "48px", padding: "0px" }}
              >
                <NavigateBefore sx={{ width: "24px", height: "24px" }} />
              </Button>
            )}

            <Typography sx={{ margin: "0 16px" }} variant="h2">
              {selectedYear + t("YEAR")}
            </Typography>

            {new Date().getFullYear() === selectedYear ? (
              <Box sx={{ width: "48px", height: "48px" }} />
            ) : (
              <Button
                onClick={() => setSelectedYear(selectedYear + 1)}
                sx={{ display: "flex", minWidth: "48px", height: "48px", padding: "0px" }}
              >
                <NavigateNext sx={{ width: "24px", height: "24px" }} />
              </Button>
            )}
          </Box>

          <LayoutVertical>
            <Card>
              <Table>
                <TableCols sizes={[40, 60]} />
                <TableBody sx={{ mariginBottom: "16px" }}>
                  <TableRow>
                    <TableCellHead sx={{ padding: "16px 8px", fontSize: "18px" }}>{t("TRANSFER_AMOUNT")}</TableCellHead>
                    <TableCell sx={{ padding: "16px 8px", fontSize: "18px" }} fontStyle="weak" align="right">
                      {toCommaString(pointTransferSummary.transfer_point)}
                      {t("YEN")}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCellHead
                      sx={{
                        padding: "16px 8px",
                        fontSize: "18px",
                        borderBottom: "none",
                      }}
                    >
                      {t("FEE_TOTAL")}
                    </TableCellHead>
                    <TableCell
                      sx={{
                        padding: "16px 8px",
                        fontSize: "18px",
                        borderBottom: "none",
                      }}
                      fontStyle="weak"
                      align="right"
                    >
                      {toCommaString(feeAmount)}
                      {t("YEN")}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCellHead sx={{ height: "36px", padding: 0 }} />
                    <TableCell
                      sx={{
                        height: "36px",
                        padding: "0px 0px 16px 0px",
                        fontSize: "14px",
                        color: COLORS.TEXT_WEAK,
                      }}
                      align="right"
                    >
                      {t("TEN_PERCENT_TAX_RATE")}
                      {toCommaString(pointTransferSummary.total_fee_tax)}
                      {t("YEN")}
                    </TableCell>
                  </TableRow>
                  {feeAmount !== 0 && (
                    <>
                      <TableExpandTrigger label={t("FEE_BREAKDOWN")} {...triggerProps("breakdown")} />
                      <TableExpandContent {...contentProps("breakdown")}>
                        <Table>
                          <TableBody>
                            <TableRow sx={{ padding: "16px 8px" }}>
                              <TableCellHead>{t("REMITTANCE_FEE")}</TableCellHead>
                              <TableCell sx={{ padding: "16px" }} fontStyle="weak" align="right">
                                {toCommaString(pointTransferSummary.transfer_fee)}
                                {t("YEN")}
                              </TableCell>
                            </TableRow>
                            <TableRow sx={{ padding: "16px 8px", fontSize: "18px" }}>
                              <TableCellHead>{t("ERROR_FEE")}</TableCellHead>
                              <TableCell sx={{ padding: "16px" }} fontStyle="weak" align="right">
                                {toCommaString(pointTransferSummary.error_fee)}
                                {t("YEN")}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell
                                colSpan={colSpan}
                                sx={{
                                  padding: "4px 0px",
                                  border: "none",
                                  color: COLORS.TEXT_WEAK,
                                  backgroundColor: COLORS.BG_01,
                                  fontSize: "14px",
                                  textAlign: "right",
                                }}
                              >
                                {t("FEE_WITH_TAX")}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableExpandContent>
                    </>
                  )}
                  <TableRow>
                    <TableCellHead bold sx={{ padding: "16px 8px", fontSize: "18px" }}>
                      {t("TOTAL")}
                    </TableCellHead>
                    <TableCell bold sx={{ padding: "16px 8px", fontSize: "20px" }} align="right">
                      {toCommaString(pointTransferSummary.amount)}
                      {t("YEN")}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Box
                sx={{
                  borderBottom: "none",
                  textAlign: "center",
                  paddingTop: "16px",
                  fontSize: "14px",
                }}
              >
                <Box sx={{ color: COLORS.TEXT_WEAK }}>{format(now, "yyyy/MM/dd HH:mm")}</Box>
                {pointTransferSummary.go_merchant_name + "(" + pointTransferSummary.go_invoice_number + ")"}
              </Box>
            </Card>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: "center",
                gap: "16px",
              }}
            >
              <DownloadSummaryPDFButton
                startAt={getStartOfYear(selectedYear)}
                endAt={getEndOfYear(selectedYear)}
                setIsLoading={setIsLoading}
              />
              <SendSummaryPDFMailButton
                startAt={getStartOfYear(selectedYear)}
                endAt={getEndOfYear(selectedYear)}
                setIsLoading={setIsLoading}
                setIsModalOpen={setIsModalOpen}
                setOpenSnackbar={setOpenSnackbar}
              />
            </Box>
          </LayoutVertical>
        </>
      )}
    </Container>
  );
};
